import { GeoServer } from '@safesystem/geoserver-api';
import {
	PUBLIC_AI_CHAT_API_URL,
	PUBLIC_AI_MAPCHAT_ENABLED,
	PUBLIC_API_HOST,
	PUBLIC_BASEMAP_NO_ROADS,
	PUBLIC_BASEMAP_FULL,
	PUBLIC_CONTEXT_LAYER_ID,
	PUBLIC_DEV_USE_PROXY,
	PUBLIC_ENVIRONMENT,
	PUBLIC_GA_TRACKING_ID,
	PUBLIC_HALO_LINK,
	PUBLIC_ISSUE_REPORTER_ENABLED,
	PUBLIC_LOGGING_MINLEVEL,
	PUBLIC_MAPTILER_KEY,
	PUBLIC_PACKAGE_VERSION,
	PUBLIC_BUILD_SHA,
	PUBLIC_SATELLITE_BASEMAP,
	PUBLIC_YEAR_COPYRIGHT_START
} from '$app/env/public';
import type { Basemap } from './apps/types';
import { applyFormat } from './apps/platform/gis-services/url-builders';
import type { Environment, LoggingLevel } from './types';

export const platformAppVersion = PUBLIC_PACKAGE_VERSION;
// Short commit SHA of the build (from Azure DevOps `BUILD_SOURCEVERSION`; empty locally).
export const platformBuildSha = PUBLIC_BUILD_SHA;
// Full build identifier — version plus commit SHA when available (e.g. "1.0.8+bead388").
export const platformBuild = platformBuildSha
	? `${platformAppVersion}+${platformBuildSha}`
	: platformAppVersion;
export const environment = PUBLIC_ENVIRONMENT as Environment;

const apiHost = PUBLIC_API_HOST;
export const geoServer = new GeoServer(apiHost);

const apiHostOrigin = (() => {
	try {
		return new URL(apiHost).origin;
	} catch {
		return null;
	}
})();

/**
 * Throw if `url` does not share an origin with `PUBLIC_API_HOST`.
 *
 * Resolved WFS template URLs come from GeoServer GetCapabilities responses, so
 * the host is server-controlled. Without this check, a compromised or
 * misconfigured server could redirect fetches to any host the CSP `connect-src`
 * allows (e.g. `www.google-analytics.com`). Pinning to the configured API
 * origin closes that gap.
 */
function assertSameOriginAsApi(url: string): void {
	if (!apiHostOrigin) {
		throw new Error(`${PUBLIC_API_HOST} is not a valid URL — cannot validate fetch origin`);
	}
	let resolvedOrigin: string;
	try {
		resolvedOrigin = new URL(url, apiHost).origin;
	} catch {
		throw new Error(`Refusing to fetch malformed URL: ${url}`);
	}
	if (resolvedOrigin !== apiHostOrigin) {
		throw new Error(`Refusing to fetch ${resolvedOrigin} — only ${apiHostOrigin} is permitted`);
	}
}

/** Fetch a WFS layer's features as JSON and return their properties. */
export async function fetchWfsProperties<T = Record<string, string | number>>(
	url: string
): Promise<T[]> {
	const resolved = applyFormat(url, 'application/json');
	assertSameOriginAsApi(resolved);
	const response = await fetch(resolved);
	if (!response.ok) throw new Error('Network response was not ok');
	const features = (await response.json()).features as { properties: T }[];
	return features.map((f) => f.properties);
}

export const lowestLoggingLevel = PUBLIC_LOGGING_MINLEVEL as LoggingLevel;

export const gaTrackingId = PUBLIC_GA_TRACKING_ID;

// Map config — used by Map.svelte and BasemapToggle.svelte
export const maptilerKey = PUBLIC_MAPTILER_KEY;
// Zoom ceiling for every map; MapLibre's own default is 22.
export const mapMaxZoom = 20;
// Fallback imagery for the satellite toggle, used by every app that doesn't set
// its own `AppConfig.satelliteBasemap` (see `satelliteSource`).
export const defaultSatelliteBasemapUrl = PUBLIC_SATELLITE_BASEMAP;

// Each app picks one of these basemaps (see `AppConfig.basemap`). The env vars
// hold full MapTiler style.json URLs; `maptilerKey` is appended by the consumer.
export const basemapStyleUrls: Record<Basemap, string> = {
	'no-roads': PUBLIC_BASEMAP_NO_ROADS,
	full: PUBLIC_BASEMAP_FULL
};
export const contextLayerId = PUBLIC_CONTEXT_LAYER_ID;

// Footer
export const copyrightStartYear = parseInt(PUBLIC_YEAR_COPYRIGHT_START);

// Halo support link — the "contact support" link in the connection & maintenance
// error modals and the embedded form's escape-hatch (error state + slow hint). It's
// the same `PUBLIC_HALO_LINK` portal URL as the embed source: opened directly, that
// URL renders the same Halo ticket form, so one env var covers both uses.
//
// The embed's escape-hatch stays removable in isolation: `SupportFormModal` passes
// `fallbackHref={haloFallbackLink}` to `SupportForm` (optional prop) — stop passing it
// and the error / slow-hint links vanish, while this link and the error modals remain.
export const haloFallbackLink = PUBLIC_HALO_LINK;

// Halo support-form embed config, derived from the `PUBLIC_HALO_LINK` portal URL
// so the ticket type has a single source of truth (and is per-env: dev/test can
// point at a non-prod queue). The in-app support form (`SupportFormModal`) passes
// these to the embed iframe; a null result falls back to the values hardcoded in
// `static/halo-form.js`. The portal URL looks like:
//   https://abley.haloitsm.com/portal/anonymousticket?tickettype_id=76&key=<uuid>
export const haloEmbedConfig = ((portalUrl: string) => {
	try {
		const url = new URL(portalUrl);
		const ticketTypeId = Number(url.searchParams.get('tickettype_id'));
		const ticketTypeKey = url.searchParams.get('key') ?? '';
		if (!ticketTypeId || !ticketTypeKey) return null;
		return { apiUrl: `${url.origin}/api`, ticketTypeId, ticketTypeKey };
	} catch {
		return null;
	}
})(PUBLIC_HALO_LINK);

// Also offers the Map & Data agent (chat mode 'data') where AI is enabled for the key; off unless set.
export const aiMapChatEnabled = PUBLIC_AI_MAPCHAT_ENABLED === 'true';

// SafeSystemAI chat host (bearer is `apiKeyStore.apiKey`); PUBLIC_DEV_USE_PROXY routes it via the Vite proxy (/aiapi) to avoid CORS.
export const aiChatApiUrl = PUBLIC_DEV_USE_PROXY === 'true' ? '/aiapi' : PUBLIC_AI_CHAT_API_URL;

// Data Issue Reporter backend — served by the same nginx as the API under
// /issue-reporter, so its URL is *derived* from PUBLIC_API_HOST rather than
// configured separately. That keeps the host a single source of truth that can
// never drift to another environment, mirroring `assertSameOriginAsApi` above.
// PUBLIC_ISSUE_REPORTER_ENABLED toggles the feature per env; an empty/invalid
// API host also yields '' (off). The report button checks for a non-empty value.
export const issueReporterUrl =
	PUBLIC_ISSUE_REPORTER_ENABLED === 'true' && apiHostOrigin
		? `${apiHostOrigin}/issue-reporter`
		: '';

export const disclaimerParts = [
	`This document has been produced for the sole use of our client. Any use of this document by a third party is without liability and you should seek independent advice. © Abley Limited ${new Date().getFullYear()}.`,
	'No part of this document may be copied without the written consent of either our client or Abley Limited. Refer to https://abley.com/output-terms-and-conditions for output terms and conditions.'
];
